import { ToastOptions, toast } from 'react-toastify';
import moment from 'moment';

import { SelectedItems, SelectedItem } from '../doc-search/config';
import * as api from '../api';
import { Product } from '../components/DocEditContent/partials/SelectedProducts';
import { loadDocEditFile } from '../services';
import { NavigateFunction } from 'react-router-dom';
import { defaultValidationSchema, businessUserValidationSchema, UserRoles } from '../constants';
import Joi from 'joi';
import { updateDocEditMetaData } from '../services/putDocEditMetaData';
import { Form } from '../pages/FormList/FormList';
import { LocalStorageFilter } from '../doc-search/SearchFilters';

export const notify = (
  message: string,
  type: 'info' | 'success' | 'warn' | 'error',
  autoClose: number = 5000,
  options?: ToastOptions
) => {
  toast[type](message, {
    autoClose,
    toastId: message,
    ...options,
  });
};

export const transformSelectedItems = (items?: SelectedItems): Array<string> | undefined => {
  if (!items || !Array.isArray(items)) {
    return undefined;
  }

  return items.map((item: SelectedItem) => (item as any).value) as Array<string>;
};

export const logDocumentAccess = async (document: string, accessType: 'download' | 'view') => {
  api.logDocumentAccess(document, accessType);
};

export const transformFilters = (
  filters?: SearchFilters | FormSetsFilters
): TransformedSearchFilters => {
  let transformedFilters: TransformedSearchFilters = {};

  if (filters) {
    transformedFilters = {
      ...filters,
      impact_on_coverage: (filters as SearchFilters).impact_on_coverage
        ? [...(filters as SearchFilters).impact_on_coverage]
        : undefined,
      form_usage_category: (filters as SearchFilters).form_usage_category
        ? [...(filters as SearchFilters).form_usage_category]
        : undefined,
      form_status: (filters as SearchFilters).form_status
        ? [...(filters as SearchFilters).form_status]
        : undefined,
      form_category: (filters as SearchFilters).form_category
        ? [...(filters as SearchFilters).form_category]
        : undefined,
      form_type_of_business: (filters as SearchFilters).form_type_of_business
        ? [...(filters as SearchFilters).form_type_of_business]
        : undefined,
      form_type: transformSelectedItems(filters.form_type),
      form_source: transformSelectedItems(filters.form_source),
      carrier: transformSelectedItems(filters.carrier),
      hierarchy: transformSelectedItems(filters.hierarchy),
      line_of_business: transformSelectedItems(filters.line_of_business),
      state_filed: transformSelectedItems(filters.state_filed),
    };
  }

  return transformedFilters;
};
export const randomId = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const toFormRule = (index: number, rule: Product) => [
  index,
  {
    businessType: rule.businessType ? 'Admitted' : 'Non-Admitted',
    product: rule.product,
    lineOfBusiness: rule.lineOfBusiness,
    unit: rule.unit,
    subunit: rule.subunit,
  },
  { isActive: rule.isActive, id: rule.id },
];

export const markDuplicateObjects = (
  keys: string[],
  array: Record<string, any>[]
): Record<string, any>[] => {
  const lookup = array.reduce((a: Record<string, number>, e) => {
    const delimeter = keys.reduce((delimeter, curr) => (delimeter = delimeter + e[curr]), '');
    a[delimeter] = ++a[delimeter] || 0;
    return a;
  }, {});

  return array.map((arr) => {
    const delimeter = keys.reduce((delimeter, curr) => (delimeter = delimeter + arr[curr]), '');
    if (lookup[delimeter]) return { ...arr, duplicate: true };
    else return { ...arr, duplicate: false };
  });
};

//TODO: send a flag from backend instead of this?
export const isElasticId = (id: string | undefined) => {
  return id && id?.charAt(8) !== '-' ? true : false;
};

export const redirectToDynamoId = async (
  id: string,
  state: AppState,
  navigate: NavigateFunction
) => {
  const response = await loadDocEditFile(id, state);

  const storedLinkData = localStorage.getItem('link');
  const linkData = storedLinkData ? JSON.parse(storedLinkData) : '';

  if (response && response.file?.data?.id && id !== response.file?.data?.id) {
    //PM user navigates to dynamo form with link data
    if (linkData) await updateDocEditMetaData(response.file.data.id, {}, { link: linkData });
    navigate(`/forms/${response.file.data.id}`);
  } else {
    //PM user navigates to elastic form, pass linkData if it exists through location.state
    navigate(`/forms/${id}`, { state: linkData ? { link: linkData } : null });
  }
};

//returns true if the user is in at least one group
export const userHasRole = (roles: UserRoles[], userRolesArray: UserRoles[]) => {
  if (!userRolesArray) return false;
  return userRolesArray.some((r) => roles.includes(r));
};
export const userHasAccess = userHasRole;

//Disabled if elasticID, PUBLISHED, SAVED, OR in EDIT by someone else
export const disableForm = (viewDoc: ViewDoc, auth: Auth) => {
  return (
    viewDoc.formSource === 'INDEX' ||
    viewDoc.status === 'PUBLISHED' ||
    (viewDoc.status === 'EDIT' && viewDoc.user.email !== auth.user.email) ||
    viewDoc.status === 'SAVED' ||
    (userHasRole([UserRoles.ADMIN], auth.roles as UserRoles[]) && viewDoc.status === 'DRAFT')
  );
};

export const getValidationSchemaByRole = (userRoles: UserRoles[]) => {
  let validationSchema;
  let narrowSchemas = [UserRoles.BUSINESSUSER];
  let scope = {
    narrow: null as any | null,
    default: null as any | null,
  };
  const getSchema = (role: UserRoles) => {
    switch (role) {
      case UserRoles.BUSINESSUSER:
        return businessUserValidationSchema;
      default:
        return defaultValidationSchema;
    }
  };
  scope = userRoles.reduce((acc, role) => {
    validationSchema = getSchema(role);
    if (narrowSchemas.includes(role)) {
      acc.narrow = validationSchema;
    } else {
      acc.default = validationSchema;
    }
    return acc;
  }, scope);

  return Joi.object(scope.narrow || scope.default).options({
    allowUnknown: true,
  });
};

export const getFormattedDate = (date: string) =>
  moment.utc(date).local().format('MM/DD/YYYY hh:mm:ss A');

export const strip = (str: string): string =>
  str
    .split(' ')
    .filter((t) => t && t.length)
    .join(' ');

export const isStatus400 = (status: number): boolean => status >= 400 && status < 500;
export const isStatus500 = (status: number): boolean => status >= 500 && status < 600;

export const groupPublogRecords = (allRecords: any) => {
  const seen = new Set();
  const recordsGroupedByPublishEvent: any = {};

  for (let i of allRecords) {
    const key = i.pk + '|' + i.timestamp;
    if (!seen.has(key)) {
      seen.add(key);
      recordsGroupedByPublishEvent[key] = [i];
    } else if (seen.has(key)) {
      recordsGroupedByPublishEvent[key].push(i);
    }
  }

  return recordsGroupedByPublishEvent;
};

export const transformToForms = (records: any) => {
  const forms: Form[] = records.map((x: any) => {
    const form: Form = {
      formNumber: x.metadata.form_number,
      formTitle: x.metadata.form_title,
      status: x.status,
      dateModified: x.timestamp,
      id: x.id,
      user: x.user,
      userEmail: x.user.email,
    };
    return form;
  });

  return forms;
};

export const loadSavedFilters = (): LocalStorageFilter[] => {
  try {
    const stored = localStorage.getItem('filters');
    if (!stored) return [];

    const filtersArray = JSON.parse(stored);

    return filtersArray.map((f: LocalStorageFilter) => ({
      ...f,
      filters: {
        ...f.filters,
        impact_on_coverage: new Set(f.filters.impact_on_coverage),
        form_usage_category: new Set(f.filters.form_usage_category),
        form_category: new Set(f.filters.form_category),
        form_type_of_business: new Set(f.filters.form_type_of_business),
        form_status: new Set(f.filters.form_status),
      },
    }));
  } catch {
    return [];
  }
};

export const saveFiltersToStorage = (filtersArray: LocalStorageFilter[]) => {
  const serialized = filtersArray.map((f: any) => ({
    ...f,
    filters: {
      ...f.filters,
      impact_on_coverage: Array.from(f.filters.impact_on_coverage),
      form_usage_category: Array.from(f.filters.form_usage_category),
      form_category: Array.from(f.filters.form_category),
      form_type_of_business: Array.from(f.filters.form_type_of_business),
      form_status: Array.from(f.filters.form_status),
    },
  }));

  localStorage.setItem('filters', JSON.stringify(serialized));
};

export const sortProductList = (list: Product[]) => {
  return list.sort((a, b) => {
    return (
      a.unit.localeCompare(b.unit) ||
      a.subunit.localeCompare(b.subunit) ||
      a.product.localeCompare(b.product) ||
      a.lineOfBusiness.localeCompare(b.lineOfBusiness)
    );
  });
};

export const sortHitList = (list: any[]) => {
  return list.sort((x, y) => {
    const a = x._source;
    const b = y._source;
    return (
      a.bu.localeCompare(b.bu) ||
      a.opu.localeCompare(b.opu) ||
      a.program_or_product.localeCompare(b.program_or_product) ||
      a.line_of_business.localeCompare(b.line_of_business)
    );
  });
};
